<template>
  <v-row>
    <v-col cols="12">
      <v-card outlined class="mb-4">
        <v-card-title>Антипассбэк</v-card-title>
        <v-divider />
        <v-card-text>
          <div class="d-flex align-center justify-space-between">
            <v-text-field
              type="number"
              label="Разрешать повторный проход через указанное кол-во минут"
              v-model="passbackTimeout"
              dense
              hide-details
            />
            <v-btn @click="update" class="primary--text ml-4" text
              >Сохранить</v-btn
            >
          </div>
        </v-card-text>
      </v-card>

      <v-card outlined>
        <v-card-title>Правила доступа</v-card-title>
        <v-divider />
        <v-card-text class="pa-2 pa-sm-4">
          <v-row dense align="center">
            <v-col cols="12" sm="auto">
              <create-rule
                @create-rule="createRule"
                @update-rule="getUserAccessPoints"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <h4 class="black--text mb-2">Доступ пользователя</h4>
              <access-rule-table
                :user-id="+$route.params.userId"
                :rules="accessRules"
                @update="getUserAccessPoints"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <v-row>
            <v-col cols="12">
              <h4 class="mb-2">Доступ категории</h4>
              <access-rule-table
                :category-id="categoryId"
                :rules="userCategoriesRules"
                :readonly="true"
                @update="getUserAccessPoints"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col v-if="!$route.path.includes('pass') && isAvailableModule('keys')">
      <v-card outlined>
        <v-card-title>Доступ к ключам</v-card-title>
        <v-divider />
        <v-card-text>
          <v-row dense align="center" class="mb-2">
            <v-col cols="12" sm="auto">
              <h4>Доступ пользователя</h4>
            </v-col>
            <v-spacer />
            <v-col cols="12" sm="auto">
              <v-text-field
                v-model="search"
                :style="searchStyle"
                hide-details
                clearable
                single-line
                prepend-icon="mdi-magnify"
                placeholder="Поиск по ключам..."
              />
            </v-col>
          </v-row>
          <keys-user-access
            v-model="keysAccessRules"
            :keys="preparKeys"
            :disabled="false"
            @set-access="setKeysAccess"
          />
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { updateUser } from "@/services/user.service";
import {
  createUserAccessPoints,
  getUserAccessPoints,
  getUserCategories,
  updateUserAccessPoint
} from "../../../services/user.service";
import AccessRuleTable from "../../rules/AccessRuleTable";
import { sortBy, intersection, map, find } from "lodash";
import CreateRule from "../../rules/CreateRule";
import { getCategoryAccessRules } from "../../../services/category.service";
import { withNotifications } from "@/utils/notifications.js";
import {
  getKeys,
  getKeyAccessRules,
  deleteKeyAccessRules,
  updateKeyAccessRules,
  findKeys
} from "@/services/keys.service";
import { isAvailableModule } from "@/services/systemConfig.service";
import KeysUserAccess from "../../keys/KeysUserAccess.vue";

export default {
  name: "AccessInformation",
  components: { CreateRule, AccessRuleTable, KeysUserAccess },

  props: {
    user: { type: Object, required: true },
    categoryList: { type: Array, default: () => [] }
  },

  watch: {
    search() {
      this.onDefaultAccess();
    }
  },

  data() {
    return {
      accessRules: [],
      keysAccessRules: [],
      keys: [],
      fkeys: [],
      preparKeys: [],
      search: null,
      userCategories: [],
      userCategoriesRules: [],
      categoryId: null
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    searchStyle() {
      return this.isMobile ? "max-width: 100%" : "max-width: 320px";
    },
    passbackTimeout: {
      get() {
        return this.user.passbackTimeout / 60;
      },
      set(value = 0) {
        this.user.passbackTimeout = value * 60;
      }
    },
    categoryKeys() {
      return this.keys.filter(
        k =>
          intersection(
            map(this.userCategories, "category.id"),
            map(k.categories, "id")
          ).length > 0
      );
    }
  },

  created() {
    this.getUserAccessPoints();
    this.getUserCategories();
    this.getKeyAccessRules();
    this.findKeys();
    this.onDefaultAccess();
  },

  methods: {
    isAvailableModule,
    getKeys() {
      withNotifications(async () => {
        this.keys = await getKeys();
      });
    },
    findKeys() {
      withNotifications(async () => {
        this.fkeys = await findKeys(this.search);
      });
    },
    getKeyAccessRules() {
      withNotifications(async () => {
        this.keysAccessRules = await getKeyAccessRules(this.user.id);
      });
    },
    async onDefaultAccess() {
      this.keys = await findKeys(this.search);
      const keysNotInCategory = this.keys.filter(
        key =>
          !this.categoryKeys.some(categoryKey => categoryKey.name === key.name)
      );
      this.preparKeys = this.keys.map(key => ({
        ...key,
        access: !keysNotInCategory.some(
          categoryKey => categoryKey.name === key.name
        )
      }));
    },
    async setKeysAccess(keyIds, accessType) {
      const keyAccessRules = keyIds.map(keyId => ({
        keyId,
        userId: this.user.id,
        accessType
      }));
      await withNotifications(async () => {
        if (accessType === null)
          await this.deleteKeyAccessRules(keyAccessRules);
        else await this.updateKeyAccessRules(keyAccessRules);
      });
    },
    async updateKeyAccessRules(keyAccessRules) {
      await updateKeyAccessRules(keyAccessRules);
      keyAccessRules.forEach(rule => {
        const found = find(this.keysAccessRules, { keyId: rule.keyId });
        found
          ? (found.accessType = rule.accessType)
          : this.keysAccessRules.push(rule);
      });
    },
    async deleteKeyAccessRules(keyAccessRules) {
      await deleteKeyAccessRules(keyAccessRules);
      this.keysAccessRules = this.keysAccessRules.filter(
        rule => !find(keyAccessRules, { keyId: rule.keyId })
      );
    },
    async update() {
      await updateUser(this.user);
    },
    async updateRule(event, rule) {
      const { userId } = this.$route.params;
      try {
        await updateUserAccessPoint(userId, {
          id: rule.id,
          name: rule.name,
          type: Number(event)
        });
      } catch (e) {
        this.$notify({
          text: e.response ? e.response.data.message : e,
          type: "error"
        });
      }
    },
    async getUserAccessPoints() {
      try {
        this.accessRules = sortBy(
          await getUserAccessPoints(this.user.id),
          "id"
        );
      } catch (e) {
        this.$notify({
          text: e.response ? e.response.data.message : e,
          type: "error"
        });
      }
    },
    async getUserCategories() {
      try {
        this.userCategories = await getUserCategories(this.user.id);
        this.categoryId = this.userCategories.map(c => c.category.id)[0];
        this.userCategoriesRules = await getCategoryAccessRules(
          this.categoryId
        );
      } catch (e) {
        this.$notify({
          text: e.response ? e.response.data.message : e,
          type: "error"
        });
      }
    },
    async createRule(params) {
      try {
        await createUserAccessPoints(this.user.id, params);
        await this.getUserAccessPoints();
      } catch (e) {
        this.$notify({
          text: e.response ? e.response.data.message : e,
          type: "error"
        });
      }
    }
  }
};
</script>
